// src/screens/ProyectosLicitacionesScreen.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import {
  IoChevronBackOutline,
  IoArrowBack,
  IoAddOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoDocumentTextOutline,
  IoEyeOutline,
  IoTimeOutline,
  IoTrashOutline,
  IoCalendarOutline,
  IoShieldCheckmarkOutline
} from 'react-icons/io5';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import AIAssistantButton from '../components/Shared/AIAssistantButton';
import './ProyectosLicitacionesScreen.css';

function ProyectosLicitacionesScreen() {
  const { userData, currentUser } = useAuth();

  // State
  const [licitaciones, setLicitaciones] = useState([]);
  const [activeTab, setActiveTab] = useState('activas'); // 'activas' | 'historial'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLicitacion, setSelectedLicitacion] = useState(null);
  
  // Form State (Create Licitacion)
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newAdminComments, setNewAdminComments] = useState('');
  const [newOpciones, setNewOpciones] = useState([
    { id: '1', proveedor: '', precio: '', tiempoEntrega: '', garantia: '', terminosPago: '', detalles: '', documentoUrl: '' },
    { id: '2', proveedor: '', precio: '', tiempoEntrega: '', garantia: '', terminosPago: '', detalles: '', documentoUrl: '' }
  ]);

  // Vote State
  const [chosenOpcionId, setChosenOpcionId] = useState('');
  const [voteComment, setVoteComment] = useState('');
  const [submittingVote, setSubmittingVote] = useState(false);

  // Simulated Document View
  const [viewingDoc, setViewingDoc] = useState(null);

  // Premium Custom Alert State
  const [alertInfo, setAlertInfo] = useState({ show: false, title: '', message: '', type: 'success' });
  const showAlert = (title, message, type = 'success') => {
    setAlertInfo({ show: true, title, message, type });
  };

  // Premium Custom Confirm State
  const [confirmInfo, setConfirmInfo] = useState({ show: false, title: '', message: '', onConfirm: null });

  // Edit & Upload States
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingLicitacion, setEditingLicitacion] = useState(null);
  const [uploadingOp, setUploadingOp] = useState({});

  const handleFileChange = async (idx, file) => {
    if (!file) return;
    const maxWeight = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxWeight) {
      showAlert('Archivo muy pesado', 'El archivo supera el límite de 5 MB permitido.', 'warning');
      return;
    }
    const optionId = newOpciones[idx].id;
    setUploadingOp(prev => ({ ...prev, [optionId]: true }));
    try {
      const storageRef = ref(storage, `licitaciones_uploads/${userData.condominioId}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);

      const updated = [...newOpciones];
      updated[idx].documentoUrl = url;
      updated[idx].documentoNombre = file.name;
      setNewOpciones(updated);
      showAlert('¡Archivo Subido!', `El documento "${file.name}" se subió exitosamente.`, 'success');
    } catch (error) {
      console.error("Error uploading document:", error);
      showAlert('Error al subir', 'Ocurrió un error al subir el documento: ' + error.message, 'error');
    } finally {
      setUploadingOp(prev => ({ ...prev, [optionId]: false }));
    }
  };

  const handleEditClick = (lic) => {
    setEditingLicitacion(lic);
    setNewTitle(lic.titulo);
    setNewDesc(lic.descripcion);
    setNewAdminComments(lic.comentariosAdmin || '');
    const ops = (lic.opciones || []).map(o => ({
      id: o.id || (Math.random().toString()),
      proveedor: o.proveedor || '',
      precio: o.precio || '',
      tiempoEntrega: o.tiempoEntrega || '',
      garantia: o.garantia || '',
      terminosPago: o.terminosPago || '',
      detalles: o.detalles || '',
      documentoUrl: o.documentoUrl || '',
      documentoNombre: o.documentoNombre || ''
    }));
    setNewOpciones(ops);
    setIsEditMode(true);
    setShowCreateModal(true);
  };

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setIsEditMode(false);
    setEditingLicitacion(null);
    setNewTitle('');
    setNewDesc('');
    setNewAdminComments('');
    setNewOpciones([
      { id: '1', proveedor: '', precio: '', tiempoEntrega: '', garantia: '', terminosPago: '', detalles: '', documentoUrl: '' },
      { id: '2', proveedor: '', precio: '', tiempoEntrega: '', garantia: '', terminosPago: '', detalles: '', documentoUrl: '' }
    ]);
  };

  const handleUpdateLicitacion = async (e) => {
    e.preventDefault();
    try {
      const parsedOpciones = newOpciones.map(op => ({
        ...op,
        precio: parseFloat(op.precio) || 0
      }));

      // Detect differences for history log
      const cambios = [];
      if (editingLicitacion.titulo !== newTitle) {
        cambios.push({ campo: 'Título', antes: editingLicitacion.titulo, despues: newTitle });
      }
      if (editingLicitacion.descripcion !== newDesc) {
        cambios.push({ campo: 'Descripción', antes: editingLicitacion.descripcion, despues: newDesc });
      }
      if ((editingLicitacion.comentariosAdmin || '') !== newAdminComments) {
        cambios.push({ campo: 'Comentarios de Administración', antes: editingLicitacion.comentariosAdmin || 'Ninguno', despues: newAdminComments || 'Ninguno' });
      }

      // Check differences in options
      const oldOps = editingLicitacion.opciones || [];
      parsedOpciones.forEach((op, index) => {
        const oldOp = oldOps.find(o => o.id === op.id) || oldOps[index];
        if (!oldOp) {
          cambios.push({
            campo: `Cotización ${op.id} (${op.proveedor || 'Nueva'})`,
            antes: 'No existía',
            despues: `Creada con precio $${(op.precio || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
          });
        } else {
          if (oldOp.proveedor !== op.proveedor) {
            cambios.push({
              campo: `Cotización ${op.id} - Proveedor`,
              antes: oldOp.proveedor || 'Sin nombre',
              despues: op.proveedor
            });
          }
          if (parseFloat(oldOp.precio) !== parseFloat(op.precio)) {
            cambios.push({
              campo: `Cotización ${op.id} (${op.proveedor}) - Precio`,
              antes: `$${(parseFloat(oldOp.precio) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
              despues: `$${(parseFloat(op.precio) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
            });
          }
          if (oldOp.tiempoEntrega !== op.tiempoEntrega) {
            cambios.push({
              campo: `Cotización ${op.id} (${op.proveedor}) - Entrega`,
              antes: oldOp.tiempoEntrega || 'Sin especificar',
              despues: op.tiempoEntrega || 'Sin especificar'
            });
          }
          if (oldOp.garantia !== op.garantia) {
            cambios.push({
              campo: `Cotización ${op.id} (${op.proveedor}) - Garantía`,
              antes: oldOp.garantia || 'Sin especificar',
              despues: op.garantia || 'Sin especificar'
            });
          }
          if (oldOp.terminosPago !== op.terminosPago) {
            cambios.push({
              campo: `Cotización ${op.id} (${op.proveedor}) - Términos de Pago`,
              antes: oldOp.terminosPago || 'Sin especificar',
              despues: op.terminosPago || 'Sin especificar'
            });
          }
          if (oldOp.detalles !== op.detalles) {
            cambios.push({
              campo: `Cotización ${op.id} (${op.proveedor}) - Detalles`,
              antes: oldOp.detalles || 'Ninguno',
              despues: op.detalles || 'Ninguno'
            });
          }
          if (oldOp.documentoNombre !== op.documentoNombre) {
            cambios.push({
              campo: `Cotización ${op.id} (${op.proveedor}) - Documento`,
              antes: oldOp.documentoNombre || 'Ninguno',
              despues: op.documentoNombre || 'Ninguno'
            });
          }
        }
      });

      // Check for deleted options
      oldOps.forEach((oldOp, index) => {
        const stillExists = parsedOpciones.some(op => op.id === oldOp.id);
        if (!stillExists && index >= parsedOpciones.length) {
          cambios.push({
            campo: `Cotización ${oldOp.id} (${oldOp.proveedor || 'Sin nombre'})`,
            antes: 'Activa',
            despues: 'Eliminada'
          });
        }
      });

      if (cambios.length === 0) {
        showAlert('Sin cambios', 'No se realizaron modificaciones.', 'warning');
        setShowCreateModal(false);
        setIsEditMode(false);
        setEditingLicitacion(null);
        return;
      }

      // Format description detail of changes
      const detalleCambio = cambios.length > 2 
        ? `Modificó ${cambios.length} campos en el proyecto` 
        : cambios.map(c => `Modificó ${c.campo}`).join(', ');

      const nuevoHistorial = [
        ...(editingLicitacion.historialCambios || []),
        {
          fecha: new Date(),
          modificadoPor: currentUser.uid,
          modificadoPorNombre: userData.nombre || 'Administración',
          detalle: detalleCambio,
          cambios: cambios
        }
      ];

      const docRef = doc(db, 'licitaciones', editingLicitacion.id);
      await updateDoc(docRef, {
        titulo: newTitle,
        descripcion: newDesc,
        comentariosAdmin: newAdminComments,
        opciones: parsedOpciones,
        historialCambios: nuevoHistorial
      });

      // Update local detailed view if currently viewing it
      if (selectedLicitacion && selectedLicitacion.id === editingLicitacion.id) {
        setSelectedLicitacion({
          ...selectedLicitacion,
          titulo: newTitle,
          descripcion: newDesc,
          comentariosAdmin: newAdminComments,
          opciones: parsedOpciones,
          historialCambios: nuevoHistorial
        });
      }

      // Reset Form and Mode
      setNewTitle('');
      setNewDesc('');
      setNewAdminComments('');
      setNewOpciones([
        { id: '1', proveedor: '', precio: '', tiempoEntrega: '', garantia: '', terminosPago: '', detalles: '', documentoUrl: '' },
        { id: '2', proveedor: '', precio: '', tiempoEntrega: '', garantia: '', terminosPago: '', detalles: '', documentoUrl: '' }
      ]);
      setShowCreateModal(false);
      setIsEditMode(false);
      setEditingLicitacion(null);
      showAlert('¡Actualizado con Éxito!', 'La licitación ha sido modificada y se ha registrado el log de auditoría.', 'success');
    } catch (err) {
      console.error(err);
      showAlert('Error al Actualizar', 'Ocurrió un error al actualizar la licitación: ' + err.message, 'error');
    }
  };

  // Fetch Licitaciones from Firestore
  useEffect(() => {
    if (!userData?.condominioId) return;

    const q = query(
      collection(db, 'licitaciones'),
      where('condominioId', '==', userData.condominioId),
      orderBy('fechaCreacion', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        fechaCreacion: docSnap.data().fechaCreacion?.toDate() || new Date()
      }));
      setLicitaciones(docs);
    });

    return () => unsubscribe();
  }, [userData]);

  // Add/Remove Quote Options in Form
  const handleAddOpcion = () => {
    if (newOpciones.length >= 5) return;
    const newId = (newOpciones.length + 1).toString();
    setNewOpciones([
      ...newOpciones,
      { id: newId, proveedor: '', precio: '', tiempoEntrega: '', garantia: '', terminosPago: '', detalles: '', documentoUrl: '' }
    ]);
  };

  const handleRemoveOpcion = (idx) => {
    if (newOpciones.length <= 2) return;
    const filtered = newOpciones.filter((_, i) => i !== idx);
    // Re-index
    const reindexed = filtered.map((op, i) => ({ ...op, id: (i + 1).toString() }));
    setNewOpciones(reindexed);
  };

  const handleOpcionChange = (idx, field, val) => {
    const updated = [...newOpciones];
    updated[idx][field] = val;
    setNewOpciones(updated);
  };

  // Submit new or edited project and quotes (Admin only)
  const handleCreateLicitacion = async (e) => {
    e.preventDefault();
    if (!newTitle || !newDesc) {
      showAlert('Campos Requeridos', 'Por favor completa el título y la descripción.', 'warning');
      return;
    }

    // Validate quotes
    for (let op of newOpciones) {
      if (!op.proveedor || !op.precio) {
        showAlert('Campos Incompletos', 'Cada cotización debe incluir al menos el Proveedor y el Precio.', 'warning');
        return;
      }
    }

    if (isEditMode && editingLicitacion) {
      await handleUpdateLicitacion(e);
      return;
    }

    try {
      const parsedOpciones = newOpciones.map(op => ({
        ...op,
        precio: parseFloat(op.precio) || 0
      }));

      const newDoc = {
        condominioId: userData.condominioId,
        titulo: newTitle,
        descripcion: newDesc,
        comentariosAdmin: newAdminComments,
        creadoPor: currentUser.uid,
        creadoPorNombre: userData.nombre || 'Administración',
        fechaCreacion: serverTimestamp(),
        estatus: 'pendiente',
        ganadorOpcionId: null,
        opciones: parsedOpciones,
        votos: {},
        historialCambios: []
      };

      await addDoc(collection(db, 'licitaciones'), newDoc);

      // Reset
      setNewTitle('');
      setNewDesc('');
      setNewAdminComments('');
      setNewOpciones([
        { id: '1', proveedor: '', precio: '', tiempoEntrega: '', garantia: '', terminosPago: '', detalles: '', documentoUrl: '' },
        { id: '2', proveedor: '', precio: '', tiempoEntrega: '', garantia: '', terminosPago: '', detalles: '', documentoUrl: '' }
      ]);
      setShowCreateModal(false);
      showAlert('¡Publicado con Éxito!', 'La licitación y sus cotizaciones han sido publicadas exitosamente.', 'success');
    } catch (err) {
      console.error(err);
      showAlert('Error al Publicar', 'Ocurrió un error al publicar la licitación: ' + err.message, 'error');
    }
  };

  // Cast vote (Committee only)
  const handleCastVote = async () => {
    if (!chosenOpcionId) {
      showAlert('Selección Requerida', 'Por favor selecciona una cotización para registrar tu voto.', 'warning');
      return;
    }
    if (!voteComment || voteComment.trim().length < 10) {
      showAlert('Justificación Incompleta', 'Por favor ingresa una justificación de al menos 10 caracteres.', 'warning');
      return;
    }

    setSubmittingVote(true);
    try {
      const updatedVotos = {
        ...(selectedLicitacion.votos || {}),
        [currentUser.uid]: {
          opcionId: chosenOpcionId,
          comentario: voteComment,
          userName: userData.nombre || 'Miembro del Comité',
          rol: 'comite',
          fecha: new Date()
        }
      };

      const docRef = doc(db, 'licitaciones', selectedLicitacion.id);
      await updateDoc(docRef, { votos: updatedVotos });

      // Refresh local selected project status
      setSelectedLicitacion({
        ...selectedLicitacion,
        votos: updatedVotos
      });

      setChosenOpcionId('');
      setVoteComment('');
      showAlert('¡Voto Registrado!', 'Tu voto ha sido registrado y firmado digitalmente en auditoría con éxito.', 'success');
    } catch (err) {
      console.error(err);
      showAlert('Error al Votar', 'Ocurrió un error al registrar el voto: ' + err.message, 'error');
    } finally {
      setSubmittingVote(false);
    }
  };

  // Close Licitacion (Admin only)
  const handleCloseLicitacion = (opcionId) => {
    setConfirmInfo({
      show: true,
      title: 'Declarar Opción Ganadora',
      message: '¿Estás seguro de que deseas cerrar la votación y declarar esta opción como ganadora? Esto generará un registro histórico definitivo.',
      onConfirm: async () => {
        try {
          const docRef = doc(db, 'licitaciones', selectedLicitacion.id);
          await updateDoc(docRef, {
            estatus: 'completada',
            ganadorOpcionId: opcionId
          });

          // Update locally
          setSelectedLicitacion({
            ...selectedLicitacion,
            estatus: 'completada',
            ganadorOpcionId: opcionId
          });

          showAlert('¡Licitación Completada!', 'El fallo ha sido registrado y archivado formalmente.', 'success');
        } catch (err) {
          console.error(err);
          showAlert('Error al Cerrar', 'Ocurrió un error al cerrar la licitación: ' + err.message, 'error');
        }
      }
    });
  };

  // Tabs Filter
  const filteredLicitaciones = licitaciones.filter(l => {
    if (activeTab === 'activas') return l.estatus === 'pendiente';
    return l.estatus === 'completada';
  });

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/licitaciones" />

      <main className="ks-main">
        <KondoBrandHeader showBack={false} />

        <div className="ks-scroll-area">
          {showCreateModal ? (
            /* CREATE VIEW INLINE (STANDALONE STYLE) */
            <div className="ks-lic-detail" style={{ maxWidth: '800px', margin: '0 auto', width: '100%', fontFamily: "'Inter', sans-serif" }}>
              {/* Premium Header Banner */}
              <div style={{ background: 'linear-gradient(135deg, #7b61ff 0%, #6366f1 100%)', position: 'relative', overflow: 'hidden', padding: '25px', borderRadius: '15px', marginBottom: '25px', boxShadow: '0 8px 20px rgba(123, 97, 255, 0.25)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', zIndex: 2, position: 'relative' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <button onClick={handleCancelCreate} style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(5px)', border: 'none', borderRadius: '50%', width: '42px', height: '42px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: 'background 0.2s', color: 'white', flexShrink: 0 }}>
                              <IoArrowBack size={22} />
                          </button>
                          <div style={{ marginTop: '0' }}>
                              <h1 style={{ color: 'white', margin: '0 0 5px', fontSize: '1.6rem', fontWeight: '800' }}>
                                  {isEditMode ? 'Editar Licitación' : 'Crear Licitación'}
                              </h1>
                              <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.9rem', fontWeight: '500' }}>
                                  {isEditMode ? 'Actualiza los datos del requerimiento y cotizaciones' : 'Registra cotizaciones para compras y obras mayores'}
                              </p>
                          </div>
                      </div>
                  </div>
                  <div style={{ position: 'absolute', right: '-15px', bottom: '-40px', zIndex: 0, pointerEvents: 'none', transform: 'rotate(-10deg)', opacity: 0.15 }}>
                      <IoDocumentTextOutline size={150} color="white" />
                  </div>
              </div>

              <div className="ks-lic-detail-info" style={{ padding: '30px', background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '16px', boxShadow: '0 10px 25px rgba(0,0,0,0.04)' }}>

                <form className="ks-lic-form-inline" onSubmit={handleCreateLicitacion} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div className="ks-form-group">
                    <label style={{ fontWeight: '600', fontSize: '12px', color: 'var(--kondo-black)', marginBottom: '4px' }}>Nombre del Proyecto / Obra Mayor *</label>
                    <input
                      type="text"
                      placeholder="ej. Impermeabilización de Torres A y B o Reparación de Elevador"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      required
                      style={{ padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(26,29,33,0.12)', outline: 'none', fontSize: '13px' }}
                    />
                  </div>

                   <div className="ks-form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <label style={{ fontWeight: '600', fontSize: '12px', color: 'var(--kondo-black)', margin: 0 }}>Descripción general del requerimiento *</label>
                      <AIAssistantButton 
                        textoOriginal={newDesc} 
                        onSugerenciaAceptada={(text) => setNewDesc(text)} 
                        contexto="Descripción de un requerimiento de obra mayor, compra o licitación en un condominio residencial."
                        style={{ padding: '4px 10px', fontSize: '0.75rem', borderRadius: '6px' }}
                      />
                    </div>
                    <textarea
                      placeholder="Detalla qué se necesita hacer, la justificación de la compra o reparación mayor..."
                      value={newDesc}
                      onChange={(e) => setNewDesc(e.target.value)}
                      rows={4}
                      required
                      style={{ padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(26,29,33,0.12)', outline: 'none', resize: 'vertical', fontSize: '13px', fontFamily: 'inherit' }}
                    />
                  </div>

                  <div className="ks-form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <label style={{ fontWeight: '600', fontSize: '12px', color: 'var(--kondo-black)', margin: 0 }}>Comentarios iniciales de Administración (Opcional)</label>
                      <AIAssistantButton 
                        textoOriginal={newAdminComments} 
                        onSugerenciaAceptada={(text) => setNewAdminComments(text)} 
                        contexto="Comentarios de la administración de un condominio acerca de la búsqueda de proveedores y justificación para una licitación."
                        style={{ padding: '4px 10px', fontSize: '0.75rem', borderRadius: '6px' }}
                      />
                    </div>
                    <textarea
                      placeholder="Agrega comentarios sobre cómo se buscaron los proveedores, criterios a evaluar, etc."
                      value={newAdminComments}
                      onChange={(e) => setNewAdminComments(e.target.value)}
                      rows={3}
                      style={{ padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(26,29,33,0.12)', outline: 'none', resize: 'vertical', fontSize: '13px', fontFamily: 'inherit' }}
                    />
                  </div>

                  <div className="ks-lic-options-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(26,29,33,0.08)', paddingTop: '20px', marginTop: '10px' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: '800', margin: 0, color: 'var(--kondo-black)' }}>Cotizaciones de Proveedores ({newOpciones.length})</h3>
                    <button
                      type="button"
                      className="ks-lic-add-op-btn"
                      onClick={handleAddOpcion}
                      disabled={newOpciones.length >= 5}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(99,72,209,0.08)', color: 'var(--kondo-purple)', border: 'none', fontSize: '12px', fontWeight: '600', padding: '8px 16px', borderRadius: '20px', cursor: 'pointer' }}
                    >
                      <IoAddOutline size={18} />
                      <span>Añadir Cotización</span>
                    </button>
                  </div>

                  <div className="ks-lic-options-stack" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {newOpciones.map((op, idx) => (
                      <div key={op.id} className="ks-lic-form-op-card" style={{ background: '#f8f9fa', border: '1px solid rgba(26,29,33,0.06)', borderRadius: '16px', padding: '20px' }}>
                        <div className="ks-lic-form-op-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--kondo-purple)' }}>Cotización / Opción {op.id}</h4>
                          {newOpciones.length > 2 && (
                            <button
                              type="button"
                              className="ks-lic-remove-op-btn"
                              onClick={() => handleRemoveOpcion(idx)}
                              style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                            >
                              <IoTrashOutline size={18} />
                            </button>
                          )}
                        </div>

                        <div className="ks-lic-form-op-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                          <div className="ks-form-group">
                            <label style={{ fontWeight: '600', fontSize: '12px', color: 'var(--kondo-black)', marginBottom: '4px' }}>Proveedor *</label>
                            <input
                              type="text"
                              placeholder="Nombre comercial"
                              value={op.proveedor}
                              onChange={(e) => handleOpcionChange(idx, 'proveedor', e.target.value)}
                              required
                              style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(26,29,33,0.12)', outline: 'none', fontSize: '13px' }}
                            />
                          </div>
                          <div className="ks-form-group">
                            <label style={{ fontWeight: '600', fontSize: '12px', color: 'var(--kondo-black)', marginBottom: '4px' }}>Precio Total ($ MXN) *</label>
                            <input
                              type="number"
                              placeholder="Monto neto total"
                              value={op.precio}
                              onChange={(e) => handleOpcionChange(idx, 'precio', e.target.value)}
                              required
                              style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(26,29,33,0.12)', outline: 'none', fontSize: '13px' }}
                            />
                          </div>
                          <div className="ks-form-group">
                            <label style={{ fontWeight: '600', fontSize: '12px', color: 'var(--kondo-black)', marginBottom: '4px' }}>Tiempo de Entrega / Obra</label>
                            <input
                              type="text"
                              placeholder="ej. 15 días hábiles"
                              value={op.tiempoEntrega}
                              onChange={(e) => handleOpcionChange(idx, 'tiempoEntrega', e.target.value)}
                              style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(26,29,33,0.12)', outline: 'none', fontSize: '13px' }}
                            />
                          </div>
                          <div className="ks-form-group">
                            <label style={{ fontWeight: '600', fontSize: '12px', color: 'var(--kondo-black)', marginBottom: '4px' }}>Garantía</label>
                            <input
                              type="text"
                              placeholder="ej. 5 años por escrito"
                              value={op.garantia}
                              onChange={(e) => handleOpcionChange(idx, 'garantia', e.target.value)}
                              style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(26,29,33,0.12)', outline: 'none', fontSize: '13px' }}
                            />
                          </div>
                          <div className="ks-form-group">
                            <label style={{ fontWeight: '600', fontSize: '12px', color: 'var(--kondo-black)', marginBottom: '4px' }}>Términos de Pago</label>
                            <input
                              type="text"
                              placeholder="ej. 50% anticipo y 50% al entregar"
                              value={op.terminosPago}
                              onChange={(e) => handleOpcionChange(idx, 'terminosPago', e.target.value)}
                              style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(26,29,33,0.12)', outline: 'none', fontSize: '13px' }}
                            />
                          </div>
                          <div className="ks-form-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <label style={{ fontWeight: '600', fontSize: '12px', color: 'var(--kondo-black)', margin: 0 }}>Detalles / Especificaciones Técnicas</label>
                              <AIAssistantButton 
                                textoOriginal={op.detalles} 
                                onSugerenciaAceptada={(text) => handleOpcionChange(idx, 'detalles', text)} 
                                contexto={`Especificaciones técnicas y detalles para una cotización de proveedor en un condominio residencial. Proveedor: ${op.proveedor}`}
                                style={{ padding: '4px 10px', fontSize: '0.75rem', borderRadius: '6px' }}
                              />
                            </div>
                            <textarea
                              placeholder="Especificaciones o comentarios adicionales de esta opción..."
                              value={op.detalles}
                              onChange={(e) => handleOpcionChange(idx, 'detalles', e.target.value)}
                              rows={3}
                              style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(26,29,33,0.12)', outline: 'none', resize: 'vertical', fontSize: '13px', fontFamily: 'inherit' }}
                            />
                          </div>

                          <div className="ks-form-group">
                            <label style={{ fontWeight: '600', fontSize: '12px', color: 'var(--kondo-black)', marginBottom: '4px' }}>Documento de Cotización (PDF o Imagen)</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <input
                                type="file"
                                accept="application/pdf,image/*"
                                onChange={(e) => handleFileChange(idx, e.target.files[0])}
                                style={{ display: 'none' }}
                                id={`file-op-${op.id}`}
                              />
                              <label
                                htmlFor={`file-op-${op.id}`}
                                style={{
                                  background: 'rgba(99, 72, 209, 0.08)',
                                  color: 'var(--kondo-purple)',
                                  padding: '10px 20px',
                                  borderRadius: '12px',
                                  fontSize: '13px',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  border: '1px dashed var(--kondo-purple)'
                                }}
                              >
                                {uploadingOp[op.id] ? 'Subiendo...' : op.documentoUrl && op.documentoUrl.startsWith('http') ? 'Cambiar Archivo' : 'Subir PDF / Imagen'}
                              </label>
                              {op.documentoNombre && (
                                <span style={{ fontSize: '12px', color: '#475569', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  📄 {op.documentoNombre}
                                </span>
                              )}
                            </div>
                          </div>

                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="ks-lic-form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid rgba(26,29,33,0.08)', paddingTop: '20px', marginTop: '10px' }}>
                    <button type="button" className="ks-lic-cancel-form-btn" onClick={handleCancelCreate} style={{ background: '#e9ecef', color: 'var(--kondo-text-muted)', border: 'none', fontSize: '13px', fontWeight: '600', padding: '10px 24px', borderRadius: '20px', cursor: 'pointer' }}>
                      Cancelar
                    </button>
                    <button type="submit" className="ks-lic-submit-form-btn" style={{ background: 'var(--kondo-purple)', color: 'var(--kondo-white)', border: 'none', fontSize: '13px', fontWeight: '600', padding: '10px 24px', borderRadius: '20px', cursor: 'pointer', boxShadow: '0 4px 10px rgba(99,72,209,0.2)' }}>
                      {isEditMode ? 'Guardar Cambios' : 'Publicar Licitación'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : !selectedLicitacion ? (
            /* LIST VIEW */
            <div className="avisos-dashboard-container">
              {/* --- Premium Banner --- */}
              <div className="muro-header-stitch">
                <div className="muro-hero-card">
                  <div className="muro-hero-content">
                    <h1>Proyectos y Licitaciones</h1>
                    <p>Compra y reparación mayor con votación colegiada.</p>
                    
                    {userData?.rol === 'admin' && (
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="hero-publish-btn"
                      >
                        <IoAddOutline size={20} />
                        <span>Nuevo</span>
                      </button>
                    )}
                  </div>

                  <IoDocumentTextOutline className="muro-hero-icon" />
                </div>
              </div>

              {/* Tab Selector */}
              <div className="ks-lic-tabs-wrapper">
                <div className="ks-lic-tabs">
                  <button
                    className={`ks-lic-tab ${activeTab === 'activas' ? 'active' : ''}`}
                    onClick={() => setActiveTab('activas')}
                  >
                    <IoTimeOutline size={18} />
                    <span>Proyectos Activos ({licitaciones.filter(l => l.estatus === 'pendiente').length})</span>
                  </button>
                  <button
                    className={`ks-lic-tab ${activeTab === 'historial' ? 'active' : ''}`}
                    onClick={() => setActiveTab('historial')}
                  >
                    <IoShieldCheckmarkOutline size={18} />
                    <span>Historial de Fallos ({licitaciones.filter(l => l.estatus === 'completada').length})</span>
                  </button>
                </div>
              </div>

              <div className="avisos-card-list licitaciones-card-list">
                {filteredLicitaciones.length === 0 ? (
                  <div className="ks-lic-empty-card">
                    <IoDocumentTextOutline size={48} className="ks-lic-empty-icon" />
                    <h3>No hay proyectos en esta sección</h3>
                    <p>Las compras y obras mayores se listan aquí para la transparencia vecinal.</p>
                  </div>
                ) : (
                  <div className="ks-lic-grid">
                    {filteredLicitaciones.map((lic) => {
                      const totalVotos = Object.keys(lic.votos || {}).length;
                      return (
                        <div
                          key={lic.id}
                          className="ks-lic-card"
                          onClick={() => setSelectedLicitacion(lic)}
                        >
                          <div className="ks-lic-card-header">
                            <span className={`ks-lic-status-badge ${lic.estatus}`}>
                              {lic.estatus === 'pendiente' ? 'En Votación' : 'Adjudicado'}
                            </span>
                            <span className="ks-lic-card-date">
                              <IoCalendarOutline size={14} />
                              {lic.fechaCreacion.toLocaleDateString()}
                            </span>
                          </div>
                          <h3>{lic.titulo}</h3>
                          <p className="ks-lic-card-desc">{lic.descripcion}</p>
                          
                          <div className="ks-lic-card-footer">
                            <div className="ks-lic-card-stat">
                              <strong>{lic.opciones?.length || 0}</strong> Cotizaciones
                            </div>
                            <div className="ks-lic-card-stat">
                              <strong>{totalVotos}</strong> Votos emitidos
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* COMPARATIVE DETAILS VIEW */
            <div className="ks-lic-detail" style={{ maxWidth: '800px', margin: '0 auto', width: '100%', fontFamily: "'Inter', sans-serif" }}>
              {/* Premium Header Banner */}
              <div style={{ background: 'linear-gradient(135deg, #7b61ff 0%, #6366f1 100%)', position: 'relative', overflow: 'hidden', padding: '25px', borderRadius: '15px', marginBottom: '25px', boxShadow: '0 8px 20px rgba(123, 97, 255, 0.25)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', zIndex: 2, position: 'relative' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <button onClick={() => setSelectedLicitacion(null)} style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(5px)', border: 'none', borderRadius: '50%', width: '42px', height: '42px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: 'background 0.2s', color: 'white', flexShrink: 0 }}>
                              <IoArrowBack size={22} />
                          </button>
                          <div style={{ marginTop: '0' }}>
                              <h1 style={{ color: 'white', margin: '0 0 5px', fontSize: '1.6rem', fontWeight: '800' }}>
                                  {selectedLicitacion.titulo}
                              </h1>
                              <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.9rem', fontWeight: '500' }}>
                                  Detalle del proyecto y evaluación de cotizaciones
                              </p>
                          </div>
                      </div>
                  </div>
                  <div style={{ position: 'absolute', right: '-15px', bottom: '-40px', zIndex: 0, pointerEvents: 'none', transform: 'rotate(-10deg)', opacity: 0.15 }}>
                      <IoDocumentTextOutline size={150} color="white" />
                  </div>
              </div>

              <div className="ks-lic-detail-info" style={{ padding: '30px', background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '16px', boxShadow: '0 10px 25px rgba(0,0,0,0.04)', position: 'relative' }}>
                {userData?.rol === 'admin' && selectedLicitacion.estatus === 'pendiente' && (
                  <button
                    type="button"
                    onClick={() => handleEditClick(selectedLicitacion)}
                    style={{
                      position: 'absolute',
                      top: '30px',
                      right: '30px',
                      background: 'rgba(99, 72, 209, 0.08)',
                      color: 'var(--kondo-purple)',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '8px 16px',
                      fontWeight: '700',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    Editar Proyecto
                  </button>
                )}
                <div className="ks-lic-detail-meta" style={{ marginBottom: '20px', paddingRight: '120px' }}>
                  <span className={`ks-lic-status-badge ${selectedLicitacion.estatus}`}>
                    {selectedLicitacion.estatus === 'pendiente' ? 'Votación Abierta' : 'Completado'}
                  </span>
                  <span>Creado por <strong>{selectedLicitacion.creadoPorNombre}</strong> el {selectedLicitacion.fechaCreacion.toLocaleDateString()}</span>
                </div>
                <p className="ks-lic-detail-desc">{selectedLicitacion.descripcion}</p>
                {selectedLicitacion.comentariosAdmin && (
                  <div className="ks-lic-admin-intro">
                    <strong>Comentarios de Administración:</strong>
                    <p>{selectedLicitacion.comentariosAdmin}</p>
                  </div>
                )}
              </div>

              {/* Side by Side Comparative Grid */}
              <div className="ks-lic-comparison-section">
                <h3>Cuadro Comparativo de Cotizaciones</h3>
                <div className="ks-lic-comparison-grid">
                  {selectedLicitacion.opciones.map((op) => {
                    const isWinner = selectedLicitacion.ganadorOpcionId === op.id;
                    const opVotos = Object.values(selectedLicitacion.votos || {}).filter(v => v.opcionId === op.id).length;
                    const totalVotos = Object.keys(selectedLicitacion.votos || {}).length;
                    const pct = totalVotos > 0 ? Math.round((opVotos / totalVotos) * 100) : 0;

                    return (
                      <div
                        key={op.id}
                        className={`ks-lic-option-card ${isWinner ? 'winner' : ''} ${chosenOpcionId === op.id ? 'selected' : ''}`}
                        onClick={() => selectedLicitacion.estatus === 'pendiente' && userData?.rol === 'comite' && setChosenOpcionId(op.id)}
                      >
                        {isWinner && <div className="ks-lic-winner-banner">👑 Ganadora / Adjudicada</div>}
                        <div className="ks-lic-option-header">
                          <span className="ks-lic-op-number">Opción {op.id}</span>
                          <h4>{op.proveedor}</h4>
                        </div>

                        <div className="ks-lic-op-price">
                          <span className="ks-lic-op-price-label">PRECIO TOTAL:</span>
                          <span className="ks-lic-op-price-value">${op.precio.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                        </div>

                        <table className="ks-lic-op-table">
                          <tbody>
                            <tr>
                              <td>⌛ Entrega:</td>
                              <td><strong>{op.tiempoEntrega || 'No especifica'}</strong></td>
                            </tr>
                            <tr>
                              <td>🛡️ Garantía:</td>
                              <td><strong>{op.garantia || 'No especifica'}</strong></td>
                            </tr>
                            <tr>
                              <td>💳 Pago:</td>
                              <td><strong>{op.terminosPago || 'No especifica'}</strong></td>
                            </tr>
                          </tbody>
                        </table>

                        {op.detalles && (
                          <div className="ks-lic-op-detalles">
                            <strong>Detalles técnicos:</strong>
                            <p>{op.detalles}</p>
                          </div>
                        )}

                        {op.documentoUrl && op.documentoUrl.startsWith('http') && (
                          <button className="ks-lic-view-doc-btn" onClick={(e) => {
                            e.stopPropagation();
                            setViewingDoc(op);
                          }}>
                            <IoEyeOutline size={16} />
                            <span>Ver Documento</span>
                          </button>
                        )}

                        {/* Votes breakdown bar */}
                        <div className="ks-lic-op-progress-block">
                          <div className="ks-lic-op-progress-header">
                            <span>Votos: {opVotos}</span>
                            <span>{pct}%</span>
                          </div>
                          <div className="ks-lic-op-progress-bar">
                            <div className="ks-lic-op-progress-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </div>

                        {/* Admin Action: Cerrar licitación declarando a este ganador */}
                        {userData?.rol === 'admin' && selectedLicitacion.estatus === 'pendiente' && (
                          <button
                            className="ks-lic-select-winner-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloseLicitacion(op.id);
                            }}
                          >
                            <IoCheckmarkCircleOutline size={16} />
                            <span>Declarar Ganador</span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Voting Action Section for Committee */}
              {userData?.rol === 'comite' && selectedLicitacion.estatus === 'pendiente' && (
                <div className="ks-lic-vote-panel">
                  <h3>Emitir Voto de Comité</h3>
                  {selectedLicitacion.votos?.[currentUser.uid] ? (
                    <div className="ks-lic-voted-banner">
                      <IoCheckmarkCircleOutline size={28} style={{ color: 'var(--kondo-teal)' }} />
                      <div>
                        <h4>Ya has emitido tu voto para este proyecto</h4>
                        <p>
                          Votaste por la <strong>Opción {
                            selectedLicitacion.opciones.find(o => o.id === selectedLicitacion.votos[currentUser.uid].opcionId)?.proveedor || 'N/A'
                          }</strong>. Justificación: <em>"{selectedLicitacion.votos[currentUser.uid].comentario}"</em>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="ks-lic-vote-form">
                      <p className="ks-lic-vote-hint">
                        💡 Selecciona una cotización en el cuadro comparativo arriba, escribe tu justificación técnica y firma tu voto.
                      </p>
                      {chosenOpcionId && (
                        <div className="ks-lic-chosen-preview">
                          Seleccionado: <strong>Opción {chosenOpcionId} - {
                            selectedLicitacion.opciones.find(o => o.id === chosenOpcionId)?.proveedor
                          }</strong>
                        </div>
                      )}
                      <div className="ks-lic-textarea-group">
                        <label htmlFor="justification">Justificación de la elección (Obligatorio para auditorías):</label>
                        <textarea
                          id="justification"
                          placeholder="Explica detalladamente por qué consideras que esta es la mejor opción (precio, garantía, reputación, tiempos)..."
                          value={voteComment}
                          onChange={(e) => setVoteComment(e.target.value)}
                          rows={4}
                        />
                        <span className="ks-char-counter">
                          {voteComment.length} caracteres (mínimo 10)
                        </span>
                      </div>
                      <button
                        className="ks-lic-submit-vote-btn"
                        onClick={handleCastVote}
                        disabled={submittingVote || !chosenOpcionId || voteComment.length < 10}
                      >
                        {submittingVote ? 'Registrando Voto...' : 'Firmar y Emitir Voto'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Committee Votes Audit Log */}
              <div className="ks-lic-audit-panel">
                <h3>Bitácora de Auditoría Vecinal</h3>
                <p className="ks-lic-audit-desc">
                  🔒 Todos los votos emitidos quedan registrados imborrablemente para futuras revisiones de cuentas o auditorías del condominio.
                </p>
                {Object.keys(selectedLicitacion.votos || {}).length === 0 ? (
                  <div className="ks-lic-audit-empty">
                    Aún no se han registrado votos para este proyecto.
                  </div>
                ) : (
                  <div className="ks-lic-audit-timeline">
                    {Object.entries(selectedLicitacion.votos).map(([userId, voto]) => {
                      const choice = selectedLicitacion.opciones.find(o => o.id === voto.opcionId);
                      return (
                        <div key={userId} className="ks-lic-audit-item">
                          <div className="ks-lic-audit-dot" />
                          <div className="ks-lic-audit-content">
                            <div className="ks-lic-audit-header">
                              <strong>{voto.userName}</strong>
                              <span className="ks-lic-audit-role">Miembro de Comité</span>
                              <span className="ks-lic-audit-date">
                                {voto.fecha?.toDate ? voto.fecha.toDate().toLocaleString() : new Date(voto.fecha).toLocaleString()}
                              </span>
                            </div>
                            <p className="ks-lic-audit-vote-text">
                              Votó por la <strong>Opción {voto.opcionId} ({choice?.proveedor || 'N/A'})</strong>
                            </p>
                            <p className="ks-lic-audit-comment">
                              💬 Justificación: <em>"{voto.comentario}"</em>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Historial de Cambios y Modificaciones (Audit Log) */}
              <div className="ks-lic-audit-panel" style={{ marginTop: '30px', borderTop: '1px solid rgba(26, 29, 33, 0.08)', paddingTop: '20px' }}>
                <h3>Historial de Cambios Administrativos</h3>
                <p className="ks-lic-audit-desc">
                  📝 Cada edición o actualización realizada por los administradores sobre los datos principales del requerimiento o sus cotizaciones se registra de manera inalterable a continuación.
                </p>
                {!selectedLicitacion.historialCambios || selectedLicitacion.historialCambios.length === 0 ? (
                  <div className="ks-lic-audit-empty">
                    No se han registrado modificaciones en este proyecto.
                  </div>
                ) : (
                  <div className="ks-lic-audit-timeline">
                    {selectedLicitacion.historialCambios.map((log, idx) => (
                      <div key={idx} className="ks-lic-audit-item">
                        <div className="ks-lic-audit-dot" style={{ backgroundColor: '#f59e0b' }} />
                        <div className="ks-lic-audit-content">
                          <div className="ks-lic-audit-header">
                            <strong>{log.modificadoPorNombre}</strong>
                            <span className="ks-lic-audit-role" style={{ background: '#fef3c7', color: '#d97706' }}>Administrador</span>
                            <span className="ks-lic-audit-date">
                              {log.fecha?.toDate ? log.fecha.toDate().toLocaleString() : new Date(log.fecha).toLocaleString()}
                            </span>
                          </div>
                           <p className="ks-lic-audit-vote-text" style={{ fontWeight: '600', marginBottom: '8px' }}>
                            {log.detalle}
                          </p>
                          {log.cambios && log.cambios.length > 0 && (
                            <ul style={{ margin: '0 0 0 16px', padding: '0', listStyleType: 'disc', fontSize: '12px', color: '#475569' }}>
                              {log.cambios.map((c, cIdx) => (
                                <li key={cIdx} style={{ marginBottom: '4px', lineHeight: '1.4' }}>
                                  <strong>{c.campo}</strong>: {c.antes !== undefined ? `de "${c.antes}"` : ''} a <strong>"{c.despues}"</strong>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* SIMULATED DOCUMENT MODAL */}
      {viewingDoc && (
        <div className="ks-lic-modal-overlay doc-viewer" onClick={() => setViewingDoc(null)}>
          <div className="ks-lic-doc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ks-lic-doc-header">
              <h3>Documento de Cotización - {viewingDoc.proveedor}</h3>
              <button onClick={() => setViewingDoc(null)}>
                <IoCloseCircleOutline size={28} />
              </button>
            </div>
             <div className="ks-lic-doc-body" style={{ padding: '24px', overflowY: 'auto' }}>
              {/* Resumen de la Cotización */}
              <div style={{
                background: 'white',
                border: '1px solid rgba(26,29,33,0.08)',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '24px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                fontFamily: "'Inter', sans-serif"
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '12px', fontWeight: '800', color: 'var(--kondo-purple)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Detalles de la Propuesta
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '12px' }}>
                    <span style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>PROVEEDOR:</span>
                    <strong style={{ fontSize: '13px', color: '#0f172a' }}>{viewingDoc.proveedor}</strong>
                  </div>
                  <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '12px' }}>
                    <span style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>PRECIO NETO:</span>
                    <strong style={{ fontSize: '15px', color: 'var(--kondo-teal)' }}>
                      ${(parseFloat(viewingDoc.precio) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </strong>
                  </div>
                  <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '12px' }}>
                    <span style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>TIEMPO DE ENTREGA:</span>
                    <strong style={{ fontSize: '12px', color: '#334155' }}>{viewingDoc.tiempoEntrega || 'No especifica'}</strong>
                  </div>
                  <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '12px' }}>
                    <span style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>GARANTÍA:</span>
                    <strong style={{ fontSize: '12px', color: '#334155' }}>{viewingDoc.garantia || 'No especifica'}</strong>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {viewingDoc.terminosPago && (
                    <div style={{ fontSize: '13px', color: '#334155', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                      <strong>Condiciones de Pago:</strong> {viewingDoc.terminosPago}
                    </div>
                  )}
                  {viewingDoc.detalles && (
                    <div style={{ fontSize: '13px', color: '#334155', background: '#f8f9fa', padding: '12px', borderRadius: '12px', marginTop: '4px' }}>
                      <strong>Especificaciones Técnicas:</strong>
                      <p style={{ margin: '6px 0 0 0', color: '#475569', lineHeight: '1.5' }}>{viewingDoc.detalles}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Botón y Visualizador del Documento */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                  <a 
                    href={viewingDoc.documentoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'linear-gradient(135deg, #7b61ff 0%, #6366f1 100%)',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '12px',
                      textDecoration: 'none',
                      fontWeight: '700',
                      fontSize: '13px',
                      boxShadow: '0 4px 12px rgba(99, 72, 209, 0.25)'
                    }}
                  >
                    <IoDocumentTextOutline size={18} />
                    Visualizar Archivo Adjunto ({viewingDoc.documentoNombre || 'Cotizacion.pdf'})
                  </a>
                </div>
                
                {/* Vista previa enmarcada */}
                <div style={{ background: 'white', padding: '12px', borderRadius: '16px', border: '1px solid rgba(26,29,33,0.08)' }}>
                  {viewingDoc.documentoUrl.toLowerCase().includes('.pdf') || (viewingDoc.documentoNombre && viewingDoc.documentoNombre.toLowerCase().includes('.pdf')) ? (
                    <iframe src={viewingDoc.documentoUrl} style={{ width: '100%', height: '400px', border: 'none', borderRadius: '8px' }} title="Documento PDF" />
                  ) : (
                    <img src={viewingDoc.documentoUrl} style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px' }} alt="Documento Adjunto" />
                  )}
                </div>
              </div>
            </div>
        </div>
      </div>
    )}

      <KondoBottomNav activeRoute="/licitaciones" />

      {/* Premium Glassmorphic Alert Modal */}
      {alertInfo.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '380px',
            padding: '30px',
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            transform: 'scale(1)',
            transition: 'transform 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontFamily: "'Inter', sans-serif"
          }}>
            {/* Icon representation */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: alertInfo.type === 'success' 
                ? 'rgba(16, 185, 129, 0.15)' 
                : alertInfo.type === 'error' 
                ? 'rgba(239, 68, 68, 0.15)' 
                : 'rgba(245, 158, 11, 0.15)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '20px',
              color: alertInfo.type === 'success' 
                ? '#10b981' 
                : alertInfo.type === 'error' 
                ? '#ef4444' 
                : '#f59e0b'
            }}>
              {alertInfo.type === 'success' ? (
                <IoCheckmarkCircleOutline size={40} />
              ) : alertInfo.type === 'error' ? (
                <IoCloseCircleOutline size={40} />
              ) : (
                <IoShieldCheckmarkOutline size={40} />
              )}
            </div>

            <h3 style={{
              margin: '0 0 10px 0',
              fontSize: '18px',
              fontWeight: '800',
              color: '#0f172a'
            }}>
              {alertInfo.title}
            </h3>

            <p style={{
              margin: '0 0 25px 0',
              fontSize: '14px',
              lineHeight: '1.5',
              color: '#475569',
              fontWeight: '500'
            }}>
              {alertInfo.message}
            </p>

            <button 
              onClick={() => setAlertInfo({ ...alertInfo, show: false })}
              style={{
                background: alertInfo.type === 'success'
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : alertInfo.type === 'error'
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  : 'linear-gradient(135deg, #7b61ff 0%, #6366f1 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '14px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                width: '100%',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.05)',
                transition: 'transform 0.15s ease'
              }}
            >
              Aceptar
            </button>
          </div>
        </div>
      )}

      {/* Premium Glassmorphic Confirmation Modal */}
      {confirmInfo.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '380px',
            padding: '30px',
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            transform: 'scale(1)',
            transition: 'transform 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontFamily: "'Inter', sans-serif"
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(99, 72, 209, 0.15)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '20px',
              color: 'var(--kondo-purple)'
            }}>
              <IoShieldCheckmarkOutline size={40} />
            </div>

            <h3 style={{
              margin: '0 0 10px 0',
              fontSize: '18px',
              fontWeight: '800',
              color: '#0f172a'
            }}>
              {confirmInfo.title}
            </h3>

            <p style={{
              margin: '0 0 25px 0',
              fontSize: '14px',
              lineHeight: '1.5',
              color: '#475569',
              fontWeight: '500'
            }}>
              {confirmInfo.message}
            </p>

            <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
              <button 
                onClick={() => setConfirmInfo({ ...confirmInfo, show: false })}
                style={{
                  background: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  flex: 1,
                  transition: 'background 0.2s'
                }}
              >
                Cancelar
              </button>
              <button 
                onClick={() => {
                  if (confirmInfo.onConfirm) confirmInfo.onConfirm();
                  setConfirmInfo({ ...confirmInfo, show: false });
                }}
                style={{
                  background: 'linear-gradient(135deg, #7b61ff 0%, #6366f1 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  flex: 1,
                  boxShadow: '0 8px 16px rgba(99, 72, 209, 0.2)'
                }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProyectosLicitacionesScreen;
